/**
 * 路由权限过滤工具。
 *
 * 背景：/api/boss/profile 与 /api/ent/profile 返回 is_super_admin 与 node_keys
 * （角色勾选的权限节点 KEY 列表）。非超管登录时，动态路由表按 rouKey 过滤：
 * 带 rouKey 的节点其值必须命中 node_keys，否则整棵子树剔除；无 rouKey 的
 * hidden 路由（详情/新增/编辑页）保留，不参与校验。
 *
 * 过滤为纯函数：逐层 map/filter 构造新对象（浅拷贝字段、替换 children/redirect），
 * component 为 () => import(...) 函数引用无法 structuredClone，故采用不可变构造，
 * 不污染 import 的原始路由表。
 */

// 拼接父级完整路径与子 path（子 path 以 '/' 开头视为绝对路径，'' 继承父级）
const joinPath = (parentPath, childPath) => {
  if (!childPath) return parentPath
  if (childPath.startsWith('/')) return childPath
  return parentPath.replace(/\/+$/, '') + '/' + childPath
}

// 节点完整路径（顶层 parentPath 传 ''）
const fullPathOf = (node, parentPath) =>
  node.path && node.path.startsWith('/') ? node.path : joinPath(parentPath, node.path)

// 第一遍：递归过滤。rouKey 未命中剔除整棵子树；children 过滤后为空且自身无 rouKey
// 的纯布局壳一并剔除（避免死 redirect 产生 404）
const filterNode = (node, keySet) => {
  if (node.rouKey && !keySet.has(node.rouKey)) return null
  if (!node.children) return { ...node }
  const children = node.children
    .map((child) => filterNode(child, keySet))
    .filter(Boolean)
  if (!children.length && !node.rouKey) return null
  return { ...node, children }
}

// 收集存活子树的完整路径与 name 集合，供 redirect 有效性判断
const collectAlive = (node, parentPath, paths, names) => {
  const full = fullPathOf(node, parentPath)
  if (full) paths.add(full)
  if (node.name) names.add(node.name)
  ;(node.children || []).forEach((child) => collectAlive(child, full, paths, names))
}

// redirect 目标是否仍指向存活路由（支持字符串 path、{ path }、{ name } 三种形态）
const isRedirectAlive = (redirect, paths, names) => {
  if (!redirect) return true
  if (typeof redirect === 'string') return paths.has(redirect)
  if (typeof redirect === 'object') {
    if (redirect.path) return paths.has(redirect.path)
    if (redirect.name) return names.has(redirect.name)
  }
  return true
}

// 深度优先找第一个存活后代的可落地完整路径（子节点 redirect 已完成修正）
const firstAlivePath = (node, parentPath, paths, names) => {
  for (const child of node.children || []) {
    const full = fullPathOf(child, parentPath)
    // 字符串 redirect 需校验存活：目标已被权限剔除时不能当作可落地，继续找后续后代
    if (typeof child.redirect === 'string') {
      if (paths.has(child.redirect)) return child.redirect
      continue
    }
    if (child.redirect && typeof child.redirect === 'object') {
      if (child.redirect.path && paths.has(child.redirect.path)) return child.redirect.path
      // 按 name 跳转且目标存活：跳到 child 自身完整路径即可触发该 redirect
      if (child.redirect.name && names.has(child.redirect.name)) return full
      continue
    }
    if (child.children && child.children.length) {
      const deep = firstAlivePath(child, full, paths, names)
      if (deep) return deep
      continue
    }
    return full
  }
  return null
}

// 第二遍：自底向上修正 redirect。目标已被剔除时改为第一个存活后代的完整路径；
// 无后代可修正的纯 redirect 兼容入口（无 children）直接剔除
const fixRedirect = (node, parentPath, paths, names) => {
  const full = fullPathOf(node, parentPath)
  const next = { ...node }
  if (node.children) {
    next.children = node.children
      .map((child) => fixRedirect(child, full, paths, names))
      .filter(Boolean)
  }
  if (isRedirectAlive(next.redirect, paths, names)) return next
  const fallback = firstAlivePath(next, full, paths, names)
  if (fallback) {
    next.redirect = fallback
    return next
  }
  // 有存活 children 但无 redirect 形态可落地：移除 redirect，由子路由自行匹配
  if (next.children && next.children.length) {
    delete next.redirect
    return next
  }
  return null
}

// 深度优先找第一个可见落地页：布局壳（meta.hidden）只递归不下钻为结果；
// 可见父级若无可见叶子后代，落其自身完整路径（redirect 已由 fixRedirect 修正指向存活子级）
const findFirstVisible = (node, parentPath) => {
  const full = fullPathOf(node, parentPath)
  if (node.children && node.children.length) {
    for (const child of node.children) {
      const hit = findFirstVisible(child, full)
      if (hit) return hit
    }
    return node.meta && node.meta.hidden ? null : full
  }
  if (node.meta && node.meta.hidden) return null
  if (node.redirect) return full
  return node.component ? full : null
}

/**
 * 按路由文件书写顺序，找权限过滤后路由表中第一个可见（非 meta.hidden）页面的完整路径。
 * 用于登录落地页动态计算：替代写死的 dashboard 地址，与侧边栏菜单顺序保持一致。
 * @param {Array} routes 过滤后的路由表（store.routerList）
 * @returns {string|null} 第一个可访问页面完整路径；无任何可见页面时返回 null
 */
export const getFirstAccessiblePath = (routes) => {
  for (const route of routes || []) {
    const hit = findFirstVisible(route, '')
    if (hit) return hit
  }
  return null
}

// 深度优先找第一个可见配置页（meta.config 且非 hidden 的叶子）的完整路径
const findFirstConfig = (node, parentPath) => {
  const full = fullPathOf(node, parentPath)
  if (node.children && node.children.length) {
    for (const child of node.children) {
      const hit = findFirstConfig(child, full)
      if (hit) return hit
    }
    return null
  }
  if (node.meta && node.meta.hidden) return null
  if (node.meta && node.meta.config && node.component) return full
  return null
}

/**
 * 按路由文件书写顺序，找权限过滤后路由表中第一个可见「企业管理配置」页面
 * （meta.config === true）的完整路径。用于企业管理入口动态落地：
 * 替代写死的 overview 地址，子账户无企业概览权限时落第一个有权限的配置页。
 * @param {Array} routes 过滤后的路由表（store.routerList）
 * @returns {string|null} 第一个可访问配置页完整路径；无任何配置权限时返回 null
 */
export const getFirstConfigPath = (routes) => {
  for (const route of routes || []) {
    const hit = findFirstConfig(route, '')
    if (hit) return hit
  }
  return null
}

/**
 * 按权限节点过滤路由表。
 * @param {Array} routes 原始路由表（boss / enterprise）
 * @param {Array} nodeKeys profile 返回的 node_keys；非数组/为空时剔除全部带 rouKey 的路由
 * @returns {Array} 过滤后的新路由数组，不修改入参
 */
export const filterRoutesByPermissions = (routes, nodeKeys) => {
  const keySet = new Set(Array.isArray(nodeKeys) ? nodeKeys : [])
  const filtered = (routes || [])
    .map((route) => filterNode(route, keySet))
    .filter(Boolean)
  const paths = new Set()
  const names = new Set()
  filtered.forEach((route) => collectAlive(route, '', paths, names))
  return filtered
    .map((route) => fixRedirect(route, '', paths, names))
    .filter(Boolean)
}

export default { filterRoutesByPermissions, getFirstAccessiblePath, getFirstConfigPath }
